export type VenueLanguageCode = string

export type LanguageMap = Record<string, string>

export const VENUE_LANGUAGE_OPTIONS: Array<{ code: VenueLanguageCode; label: string; native: string }> = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'ta', label: 'Tamil', native: 'தமிழ்' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'ml', label: 'Malayalam', native: 'മലയാളം' },
  { code: 'te', label: 'Telugu', native: 'తెలుగు' },
  { code: 'kn', label: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ar', label: 'Arabic', native: 'العربية' },
  { code: 'si', label: 'Sinhala', native: 'සිංහල' },
  { code: 'fr', label: 'French', native: 'Français' },
  { code: 'es', label: 'Spanish', native: 'Español' },
  { code: 'de', label: 'German', native: 'Deutsch' },
]

export const getVenueLanguageLabel = (code: VenueLanguageCode): string => {
  const key = String(code || '').trim().toLowerCase()
  const match = VENUE_LANGUAGE_OPTIONS.find((item) => item.code === key)
  return match ? `${match.label} (${match.native})` : key.toUpperCase()
}

export const normalizeLanguageCodes = (value: unknown, fallback: VenueLanguageCode[] = ['en']): VenueLanguageCode[] => {
  const codes: VenueLanguageCode[] = []
  if (Array.isArray(value)) {
    value.forEach((item) => {
      const code = String(item || '').trim().toLowerCase()
      if (code && !codes.includes(code)) {
        codes.push(code)
      }
    })
  }
  return codes.length ? codes : fallback.map((code) => String(code).trim().toLowerCase()).filter(Boolean)
}

export const toLanguageMap = (value: unknown, primaryLanguage: VenueLanguageCode = 'en'): LanguageMap => {
  if (typeof value === 'string') {
    const text = value.trim()
    return text ? { [primaryLanguage]: text } : {}
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {}
  }
  const map: LanguageMap = {}
  Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
    const code = String(key || '').trim().toLowerCase()
    if (!code || typeof entry !== 'string') return
    const text = entry.trim()
    if (text) map[code] = text
  })
  return map
}

export const resolveLanguageMapValue = (
  value: unknown,
  language: VenueLanguageCode,
  primaryLanguage: VenueLanguageCode = 'en',
): string => {
  const map = toLanguageMap(value, primaryLanguage)
  return map[language] || map[primaryLanguage] || map.en || Object.values(map)[0] || ''
}

/** Exact map entry for the editor language — no fallback (empty means "not translated yet"). */
export const getLanguageMapValue = (
  value: unknown,
  language: VenueLanguageCode,
  primaryLanguage: VenueLanguageCode = 'en',
): string => {
  const map = toLanguageMap(value, primaryLanguage)
  const code = String(language || primaryLanguage).trim().toLowerCase() || primaryLanguage
  return map[code] || ''
}

export const setLanguageMapValue = (
  current: unknown,
  language: VenueLanguageCode,
  nextValue: string,
  primaryLanguage: VenueLanguageCode = 'en',
): LanguageMap => {
  const map = toLanguageMap(current, primaryLanguage)
  const code = String(language || primaryLanguage).trim().toLowerCase() || primaryLanguage
  const text = String(nextValue ?? '').trim()
  if (text) {
    map[code] = text
  } else {
    delete map[code]
  }
  return map
}
